import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTRPC } from '../../lib/trpc';
import { photoHoldMessage } from '../../lib/image';
import { useSession } from '../session';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { IconSettings } from '../icons';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';

const bytes = (n: number) =>
  n >= 1_000_000_000
    ? `${Number((n / 1_000_000_000).toFixed(2))} GB`
    : `${Number((n / 1_000_000).toFixed(2))} MB`;

/** Server-owned allowances and recovery, available to the people who manage the shop. */
export function PhotoStorageCard({ id }: { id: string }) {
  const { role } = useSession();
  const allowed = role === 'owner' || role === 'office';
  const trpc = useTRPC(),
    qc = useQueryClient();
  const [trashCursors, setTrashCursors] = useState<string[]>([]);
  const after = trashCursors.at(-1);
  const policy = useQuery({
    ...trpc.attachment.mediaPolicy.queryOptions(),
    enabled: allowed,
  });
  const usage = useQuery({
    ...trpc.attachment.mediaUsage.queryOptions(),
    enabled: allowed,
  });
  const trash = useQuery({
    ...trpc.attachment.mediaTrash.queryOptions(after ? { after } : undefined),
    enabled: allowed,
  });
  const hasNext = trash.data?.length === 200;
  const restore = useMutation(
    trpc.attachment.restore.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: trpc.attachment.pathKey() });
      },
    }),
  );
  const usageState = shownState(usage, usage.data);
  const policyState = shownState(policy, policy.data);
  const trashState = shownState(trash, trash.data);
  if (!allowed) return null;
  return (
    <SectionCard id={id} title="Photo storage">
      {!usage.data ? (
        <UnresolvedState
          state={unresolved(usageState)}
          what="photo storage"
          icon={IconSettings}
          error={usage.error}
          onRetry={() => void usage.refetch()}
        />
      ) : (
        <div className="space-y-3">
          <StaleNotice
            show={refreshFailed(usage, usage.data)}
            onRetry={() => void usage.refetch()}
          />
          {usage.data.known ? (
            <>
              <p className="text-sm">
                Company allowance: <strong>{bytes(usage.data.limitBytes)}</strong>
              </p>
              <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-muted-foreground">Stored</dt>
                  <dd className="font-semibold">{bytes(usage.data.physicalBytes)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Reserved for uploads</dt>
                  <dd className="font-semibold">{bytes(usage.data.reservedBytes)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">In trash</dt>
                  <dd className="font-semibold">{bytes(usage.data.trashBytes)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Available</dt>
                  <dd className="font-semibold">{bytes(usage.data.availableBytes)}</dd>
                </div>
              </dl>
              <p className="text-xs text-muted-foreground">
                Stored includes originals, previews and photos in trash. Reserved space is
                held for uploads that have not finished.
              </p>
            </>
          ) : (
            <p className="text-sm">
              We’re checking the company’s photo storage. Space available is unknown until
              that check finishes.
            </p>
          )}
          {usage.data.uploadsPaused ? (
            <Notice tone="warn">
              Uploads are paused. {photoHoldMessage(usage.data.blockedReason, false)}
            </Notice>
          ) : null}
        </div>
      )}
      <div className="mt-4 border-t border-border pt-4">
        <h3 className="text-sm font-semibold">Photo trash</h3>
        {policyState === 'ready' && policy.data ? (
          <p className="mt-1 text-sm text-muted-foreground">
            Removed photos stay in trash for at least {policy.data.trashDays} days and
            still use storage. You can restore them until permanent removal begins.
            Restoring keeps the photo on its original record.
          </p>
        ) : (
          <UnresolvedState
            state={unresolved(policyState)}
            what="photo retention"
            icon={IconSettings}
            error={policy.error}
            onRetry={() => void policy.refetch()}
          />
        )}
        <StaleNotice
          show={refreshFailed(policy, policy.data)}
          onRetry={() => void policy.refetch()}
        />
        {restore.error ? <Notice tone="danger">{restore.error.message}</Notice> : null}
        {!trash.data ? (
          <UnresolvedState
            state={unresolved(trashState)}
            what="photo trash"
            icon={IconSettings}
            error={trash.error}
            onRetry={() => void trash.refetch()}
          />
        ) : (
          <>
            <StaleNotice
              show={refreshFailed(trash, trash.data)}
              onRetry={() => void trash.refetch()}
            />
            {trash.data.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                {after ? 'No more photos in trash.' : 'No photos in trash.'}
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-border">
                {trash.data.map((photo) => (
                  <li
                    key={photo.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <div className="min-w-0">
                      <p className="break-words text-sm font-medium">
                        {photo.filename ?? 'Photo'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {bytes(photo.byteSize)} · Eligible for removal from{' '}
                        {new Date(photo.purgeAfter).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      disabled={!photo.canRestore || restore.isPending}
                      onClick={() => restore.mutate({ attachmentId: photo.id })}
                    >
                      {photo.canRestore ? 'Restore' : 'Removal started'}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {trashCursors.length > 0 || hasNext ? (
          <nav
            aria-label="Photo trash pages"
            className="mt-3 flex flex-wrap items-center gap-3"
          >
            <Button
              variant="ghost"
              className="min-h-11"
              disabled={trashCursors.length === 0 || restore.isPending}
              onClick={() => setTrashCursors((cursors) => cursors.slice(0, -1))}
            >
              Previous
            </Button>
            <p className="text-sm text-muted-foreground" aria-live="polite">
              Page {trashCursors.length + 1}
              {trash.data ? ` · ${trash.data.length} photos shown` : ''}
            </p>
            <Button
              variant="ghost"
              className="min-h-11"
              disabled={!hasNext || trash.isFetching || restore.isPending}
              onClick={() => {
                const last = trash.data?.at(-1);
                if (last) setTrashCursors((cursors) => [...cursors, last.id]);
              }}
            >
              Next
            </Button>
          </nav>
        ) : null}
      </div>
    </SectionCard>
  );
}
